/* A collapsible block that is not a native <details>.
 *
 * WHY NOT <details>
 * -----------------
 * The 2026-09 audit counted 13 native ``<details>`` across 8 builder
 * files and one in the console. Each renders the operating system's own
 * triangle, at the OS's own size and colour, inside a page whose every
 * other affordance comes from the kit — the same "this control is not
 * ours" tell as the native ``<select>``. Safari adds a marker the CSS
 * reset does not reach, and the summary line ignores the type scale.
 *
 * @omnitok/ui has no disclosure of its own (checked against its .d.ts:
 * Alert, Badge, Button, Card, Checkbox, CodeEditor, ConfirmModal,
 * Dropdown, DropdownMenu, FileDropzone, Input, Modal, MultiSelect,
 * Pagination, Radio, Select, Table, Tabs, Textarea, Toast — no
 * accordion), so it lives here, once, instead of being hand-rolled per
 * screen.
 *
 * ``summary`` is a ReactNode, not a string: callers pass a label plus a
 * count, or a <code> chip with the table name.
 */

import { ChevronRight } from "lucide-react";
import { useId, useState, type ReactNode } from "react";

import { cx } from "./layout";

export interface DisclosureProps {
  /** The always-visible row. A node, so it can carry a count or a chip. */
  summary: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  /** Drop the border and background — for a disclosure inside a Section. */
  bare?: boolean;
  /** Extra classes on the summary row. A chapter-sized row needs more
   *  padding than a schema row; everything else stays shared. */
  summaryClassName?: string;
  className?: string;
}

export function Disclosure({
  summary,
  children,
  defaultOpen = false,
  bare = false,
  summaryClassName,
  className,
}: DisclosureProps): ReactNode {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  return (
    <div
      className={cx(
        !bare && "rounded-xl border border-neutral-200 bg-white",
        className,
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((v) => !v)}
        className={cx(
          "flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-neutral-800",
          "transition-colors hover:bg-primary-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40",
          !bare && "rounded-xl",
          open && !bare && "rounded-b-none border-b border-neutral-200",
          summaryClassName,
        )}
      >
        <ChevronRight
          size={14}
          aria-hidden="true"
          className={cx(
            "flex-shrink-0 text-neutral-500 transition-transform",
            open && "rotate-90",
          )}
        />
        <span className="min-w-0 flex-1">{summary}</span>
      </button>
      {open && (
        <div id={bodyId} className={bare ? "pt-2" : "px-3 pb-3 pt-2"}>
          {children}
        </div>
      )}
    </div>
  );
}
